import React from 'react';
import { Tag, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { orderPropTypes } from '../../../types';
import { creationProblemLabel, deriveOrderAttention } from '../../../utils/creationProblems';

/**
 * PORTAL-6207 / PORTAL-6209 — a "needs attention" flag for an order's unresolved data-quality
 * creation problems (an unresolvable plate, an unlocatable stop, …). Renders a labelled warning
 * Tag ("Needs attention") rather than a bare icon, so it reads at a glance in a dense list rather
 * than getting lost (PORTAL-6209). Hovering/focusing the tag reveals a tooltip listing each active
 * problem. Renders nothing when the order is clean (or its problems have all been resolved by a
 * later update — see deriveOrderAttention), so it can be dropped into a table cell unconditionally.
 */
const OrderAttentionIndicator = ({ order }) => {
  const { t } = useTranslation();
  const problems = deriveOrderAttention(order);
  if (!problems.length) return null;

  return (
    <Tooltip
      trigger={['hover', 'focus']}
      title={(
        <div className="aiio-errors-tooltip">
          <div className="aiio-fw-500" style={{ marginBottom: 4 }}>
            {t('ORDER.CREATION_PROBLEMS.TITLE')}
          </div>
          {problems.map((problem) => (
            <div key={`${problem.code}-${problem.fieldPath}`} className="aiio-error-item">
              <div className="aiio-fw-500">{creationProblemLabel(t, problem.code)}</div>
              {problem.message ? <div>{problem.message}</div> : null}
            </div>
          ))}
        </div>
      )}
    >
      <Tag
        color="warning"
        icon={<WarningOutlined aria-hidden />}
        tabIndex={0}
        style={{ margin: 0 }}
      >
        {t('ORDER.CREATION_PROBLEMS.TITLE')}
      </Tag>
    </Tooltip>
  );
};

OrderAttentionIndicator.propTypes = {
  order: orderPropTypes,
};

OrderAttentionIndicator.defaultProps = {
  order: null,
};

export default OrderAttentionIndicator;
